import { assign, createMachine } from 'xstate';
import { GO_SOLAR_FUNNEL_ID } from '~/funnels/go-solar/config';
import { setError } from '~/machines/utils/actions';
import { contextCreator } from '~/machines/utils/context';
import { LDClient, LDFlags } from '~/providers';
import { fetchUtility } from '~/services';
import { FetchUtilityResponse } from '~/services/fetch-utility';
import { FunnelTrack } from '~/typings/graphql';
import { INTERNAL_ROUTES } from '~/utils/constants';
import { hardRedirect } from '~/utils/for-warbler';
import { sendToSegment } from '~/utils/tracking';
import { isBlockedUtility } from '../solar-funnel/machine';
import {
  UtilityContext,
  UtilityDoneData,
  UtilityEvent,
  UtilityServices,
  UtilityStates,
} from './types';

export const utilityMachine = () =>
  createMachine(
    {
      tsTypes: {} as import('./machine.typegen').Typegen0,
      predictableActionArguments: true,
      schema: {
        context: {} as UtilityContext,
        events: {} as UtilityEvent,
        services: {} as UtilityServices,
      },
      context: {
        errors: {},
        utilityId: undefined,
        zipCode: undefined,
        queryParams: {},
        forceWaitlist: false,
      },
      initial: 'start',
      states: {
        start: {
          entry: 'trackStarted',
          always: [{ cond: 'hasUtilityAndZipCode', target: 'bouncer' }, { target: 'utility' }],
        },
        utility: {
          id: 'utility',
          on: {
            SUBMIT_UTILITY: { target: 'bouncer', actions: 'saveUtilitySubmission' },
            UPDATE_PROJECT_ID: {
              actions: 'setFunnelVnmProjectId',
            },
            INVALID_PROJECT_ID: { target: '#invalidProjectId' },
          },
        },
        bouncer: {
          id: 'bouncer',
          initial: 'fetching',
          states: {
            fetching: {
              invoke: {
                id: 'fetchUtility',
                src: 'fetchUtility',
                onDone: { target: 'deciding', actions: 'saveUtility' },
                onError: { target: '#utility', actions: 'setUtilityError' },
              },
            },
            deciding: {
              always: [
                { cond: 'shouldRedirect', target: '#redirect' },
                { cond: 'shouldShowFriction', target: '#friction' },
                { target: '#completed' },
              ],
            },
          },
        },
        invalidProjectId: {
          id: 'invalidProjectId',
        },
        completed: {
          id: 'completed',
          type: 'final',
          entry: 'trackCompleted',
          data: (context): UtilityDoneData => {
            return {
              utility: context.utility!,
              zipCode: context.zipCode!,
              forceWaitlist: context.forceWaitlist!,
              funnelVnmProjectId: context.funnelVnmProjectId!,
            };
          },
        },
        friction: {
          id: 'friction',
        },
        redirect: {
          id: 'redirect',
          entry: 'handleRedirect',
        },
      },
    },
    {
      /* eslint-disable @typescript-eslint/no-unused-vars */
      // TLDR: need the unused _context - https://xstate.js.org/docs/guides/typescript.html#troubleshooting
      actions: {
        handleRedirect: context => {
          const redirectPath = context.redirect?.({
            queryParams: context.queryParams,
            utility: context.utility!,
            zipCode: context.zipCode!,
          });
          hardRedirect(redirectPath || INTERNAL_ROUTES.UNEXPECTED);
        },
        saveUtility: assign((_context, event) => ({
          utility: event.data.utility,
        })),
        setFunnelVnmProjectId: assign((_context, event) => ({
          funnelVnmProjectId:
            event.funnelVnmProjectId ||
            LDClient.variation(LDFlags.ffDefaultSignupProjectId)?.project_id,
        })),
        saveUtilitySubmission: assign((_context, event) => ({
          utilityId: event.data.utilityId,
          zipCode: event.data.zipCode,
        })),
        setUtilityError: assign((context, event) => ({
          errors: setError<UtilityStates>('utility', context, event),
        })),
        trackCompleted: context => {
          sendToSegment({
            type: 'utility_step_completed',
            utilityId: context.utilityId!,
            zipCode: context.zipCode!,
            funnelTrack: context.utility?.funnelTrack,
          });
        },
        trackStarted: context => {
          sendToSegment({
            type: 'utility_step_started',
            utilityId: context.utilityId,
            zipCode: context.zipCode,
          });
        },
      },
      /* eslint-disable @typescript-eslint/no-unused-vars */
      guards: {
        hasUtilityAndZipCode: context => !!(context.utilityId && context.zipCode),
        shouldRedirect: context => {
          const redirectPath = context.redirect?.({
            queryParams: context.queryParams,
            utility: context.utility!,
            zipCode: context.zipCode!,
          });
          return !!redirectPath;
        },
        shouldShowFriction: () => false,
      },
      services: {
        fetchUtility: async context => {
          const utility = await fetchUtility({ utilityId: context.utilityId! });
          const newUtility: FetchUtilityResponse = JSON.parse(JSON.stringify(utility));
          if (isWaitlist(context, newUtility)) {
            newUtility.funnelTrack = FunnelTrack.Waitlist;
            context.forceWaitlist = true;
          }

          return { utility: newUtility };
        },
      },
    },
  );

export const isWaitlist = (context: UtilityContext, newUtility: FetchUtilityResponse): boolean => {
  if (newUtility) {
    const { utilityId, funnelId } = context;

    if (utilityId === 2326 && funnelId === 'business') {
      return false;
    }
    if (isBlockedUtility(utilityId!)) {
      return true;
    }
    if (isNMWaitlist(context, newUtility)) {
      return true;
    }
  }
  return false;
};

export const isNMWaitlist = (
  context: UtilityContext,
  newUtility: FetchUtilityResponse,
): boolean => {
  const { funnelId, queryParams } = context;
  const { unparsedQueryParams } = queryParams;
  const isNM = newUtility?.state?.abbreviation === 'NM';
  const isGoSolar = funnelId === GO_SOLAR_FUNNEL_ID;
  const hasValidProjectId = validateProjectId(unparsedQueryParams?.projectId);

  if (isNM) {
    return !(isGoSolar && hasValidProjectId);
  }
  return false;
};

const validateProjectId = (projectId: any): boolean => {
  const allowedNMSolarProjectIds = LDClient.variation(LDFlags.nmUtilitiesAllowedProjectIds);
  return allowedNMSolarProjectIds?.includes(projectId);
};

export const utilityContext = contextCreator<UtilityContext>(utilityMachine, queryParams => ({
  utilityId: queryParams.utilityId,
  zipCode: queryParams.zipCode,
  queryParams,
}));
